import FavoriteIcon from '@mui/icons-material/Favorite';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useId, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { testIdAttr } from '@/lib/testIds';

import { LINK_PUBLISHED, volunteerHint, type Arc, type HeaderAction } from './arcTypes';

/**
 * The hint, plus why the button is not in the app yet (P8, backend B15). The
 * switch itself is disabled until the link is published (organization-arc
 * D4, P39).
 */
function linkHint(base: string, linkStatus: string): string {
  return linkStatus === LINK_PUBLISHED
    ? base
    : `${base} · not published, the button stays hidden`;
}

interface HeaderActionsCardProps {
  arc: Arc;
  disabled: boolean;
  onChange: (action: HeaderAction, enabled: boolean) => void;
}

/**
 * The Donate and Volunteer buttons at the top of the ARC, each switched on or
 * off here; Save Draft saves the change. What each opens is set up on its own
 * page, which the button under it goes to. Laid out as the wireframe draws it
 * (Part 2, page 4): side by side, each in a box with its icon and its state.
 *
 * A switch can be changed only while its link is published (group 6, D4):
 * until then it is disabled. A switch left on when its link is unpublished
 * keeps its saved value, reads "Link not published", and the app hides the
 * button until the link is published again (D5).
 */
export function HeaderActionsCard({ arc, disabled, onChange }: HeaderActionsCardProps) {
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby="arc-builder-actions-card-heading"
      data-testid="arc-builder-actions-card"
      sx={{ px: 3, py: 2.5 }}
    >
      <Typography
        id="arc-builder-actions-card-heading"
        variant="subtitle1"
        component="h2"
        sx={{ fontWeight: 600 }}
      >
        Header Actions
      </Typography>
      <Typography variant="body2">
        Fixed prominent actions shown below the organization bio in the ARC header.
      </Typography>

      <Box
        data-testid="arc-builder-actions-card-body"
        sx={{
          mt: 2,
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        <HeaderAction
          icon={<MonetizationOnOutlinedIcon />}
          label="Donate"
          hint={linkHint('External fundraising link', arc.header.donate.link_status)}
          checked={arc.header.donate.enabled}
          linkPublished={arc.header.donate.link_status === LINK_PUBLISHED}
          onChange={(enabled) => onChange('donate', enabled)}
          manage={{ label: 'Manage Fundraising', to: ROUTES.arc.fundraising }}
          disabled={disabled}
          testId="arc-builder-donate"
        />
        <HeaderAction
          icon={<FavoriteIcon />}
          label="Volunteer"
          hint={linkHint(
            volunteerHint(arc.header.volunteer.mode),
            arc.header.volunteer.link_status,
          )}
          checked={arc.header.volunteer.enabled}
          linkPublished={arc.header.volunteer.link_status === LINK_PUBLISHED}
          onChange={(enabled) => onChange('volunteer', enabled)}
          manage={{ label: 'Manage Volunteers', to: ROUTES.arc.volunteer }}
          disabled={disabled}
          testId="arc-builder-volunteer"
        />
      </Box>
    </Paper>
  );
}

interface HeaderActionProps {
  icon: ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  /** The link the button opens is published, so the switch may be changed. */
  linkPublished: boolean;
  onChange: (enabled: boolean) => void;
  manage: { label: string; to: string };
  disabled: boolean;
  /**
   * `<testId>` on the box, `-switch` on the switch's input (for `check()`),
   * `-hint`, `-value` (ON or OFF) and `-link` on the Manage button.
   */
  testId: string;
}

/**
 * One header button: its icon, name and what it opens, and a switch whose
 * state reads ON or OFF beside it. The name labels the switch and the hint is
 * read with it, so a screen reader hears "Donate, switch, on, External
 * fundraising link".
 */
function HeaderAction({
  icon,
  label,
  hint,
  checked,
  linkPublished,
  onChange,
  manage,
  disabled,
  testId,
}: HeaderActionProps) {
  const labelId = useId();
  const hintId = useId();
  const messageId = useId();
  // On, with nothing for the app to open: the saved value stands (D5).
  const linkMessage = checked && !linkPublished ? 'Link not published' : null;
  return (
    // The box takes any extra height, so both boxes match and the Manage
    // buttons line up when one hint runs longer (P8).
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        justifyItems: 'center',
        gridTemplateRows: '1fr auto',
      }}
    >
      <Box
        data-testid={testId}
        sx={{
          width: '100%',
          border: 1.5,
          borderColor: 'text.primary',
          borderRadius: 1,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', flexShrink: 0 }} aria-hidden>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography id={labelId} variant="body1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography id={hintId} variant="body2" data-testid={`${testId}-hint`}>
            {hint}
          </Typography>
          {linkMessage ? (
            <Typography
              id={messageId}
              variant="body2"
              color="warning.dark"
              data-testid={`${testId}-link-message`}
            >
              {linkMessage}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Typography
            variant="body2"
            color={checked ? 'primary' : 'text.secondary'}
            data-testid={`${testId}-value`}
            aria-hidden
            sx={{ fontWeight: 700 }}
          >
            {checked ? 'ON' : 'OFF'}
          </Typography>
          <Switch
            checked={checked}
            disabled={disabled || !linkPublished}
            onChange={(event) => onChange(event.target.checked)}
            slotProps={{
              input: {
                'aria-labelledby': labelId,
                'aria-describedby': linkMessage ? `${hintId} ${messageId}` : hintId,
                ...testIdAttr(`${testId}-switch`),
              },
            }}
          />
        </Box>
      </Box>
      <Button
        variant="secondary"
        size="sm"
        component={RouterLink}
        to={manage.to}
        data-testid={`${testId}-link`}
      >
        {manage.label}
      </Button>
    </Box>
  );
}
